/** why: evidência e valores de diff variam por verificação (string, número, lista, objeto, aninhado); um formatador
 * recursivo único evita "[object Object]" e o texto literal "null"/"undefined"/"true"/"false" em qualquer lugar da
 * tela, aplica os rótulos de chave/enum do dicionário (evidenceLabels.ts) e converte data ISO e uuid de contrato
 * para texto legível. Reaproveitado por FindingCard.tsx (evidência e mudança de evidência) e RaioXSummary.tsx
 * (tabela de campos alterados). invariant: mesmo comportamento e mesma tabela de casos de
 * report/evidence-format.ts do backend (evidenceFormat.spec.ts espelha evidence-format.spec.ts). */

import { formatCnpj } from "utils/federal-tax-id";
import {
  CNPJ_KEYS,
  DATA_KEYED_OBJECTS,
  EVIDENCE_ENUM_LABEL,
  EVIDENCE_KEY_LABEL,
  WORKSPACE_ID_KEYS,
  WORKSPACE_MAP_KEYS,
} from "./evidenceLabels";

export interface IEvidenceFormatOptions {
  workspaceLabels?: Record<string, string>;
}

export function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

/** hazard: busca por índice simples num objeto literal acha as propriedades de Object.prototype
 * ({ constructor: 1 } viraria o texto da função Object); só a própria chave do dicionário conta.
 * invariant: literal do backend (report/evidence-format.ts, ownValue). */
export function ownValue<T>(dict: Record<string, T> | undefined, key: string): T | undefined {
  return dict !== undefined && Object.prototype.hasOwnProperty.call(dict, key) ? dict[key] : undefined;
}

/** why: mesma fórmula que RaioX.tsx e RaioXSections.tsx já usavam inline para o nome do contrato; centralizada
 * aqui para não divergir entre os dois lugares e para uso dentro de formatValue/formatPairs. */
export function workspaceLabelOf(id: string, labels: Record<string, string> = {}): string {
  return ownValue(labels, id) ?? `Contrato ${id.slice(0, 8)}`;
}

export function evidenceKeyLabel(key: string): string {
  return ownValue(EVIDENCE_KEY_LABEL, key) ?? key;
}

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const INSTANT_RE = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?(Z|[+-]\d{2}:\d{2})$/;

/** why: valida a data escrita no texto (não a convertida): Date.UTC com o mesmo ano, mês e dia
 * tem de devolver os mesmos três números. invariant: literal do backend (isRealDay). */
function isRealDay(y: string, m: string, d: string): boolean {
  const check = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)));
  return check.getUTCFullYear() === Number(y) && check.getUTCMonth() === Number(m) - 1 && check.getUTCDate() === Number(d);
}

function formatToPartsMap(date: Date): Record<string, string> {
  const parts = new Intl.DateTimeFormat("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const out: Record<string, string> = {};
  parts.forEach((p) => {
    out[p.type] = p.value;
  });
  return out;
}

/** why: dd/mm/aaaa sem fuso para data pura (não há hora a converter); instante ISO converte para
 * America/Sao_Paulo e só mostra a hora quando ela não é 00:00 local (as datas da configuração são
 * gravadas como 03:00Z, meia-noite em São Paulo, e sairiam todas "01/08/2024 00:00").
 * hazard: o V8 aceita "2026-02-30T00:00:00Z" (e "-03:00", e hora 24) e rola para o dia seguinte;
 * por isso dia, mês e ano do texto passam por isRealDay e a hora literal tem de estar em 00-23, o
 * minuto e o segundo em 00-59, antes de converter, com qualquer fuso (Z ou offset). Sem essas
 * guardas, uma data inválida sairia como outra data plausível, escondendo o defeito em vez de
 * mostrar o texto como veio. invariant: mesma guarda do backend (report/evidence-format.ts). */
export function formatEvidenceDate(value: string): string | null {
  const dateOnly = DATE_ONLY_RE.exec(value);
  if (dateOnly) {
    const [, y, m, d] = dateOnly;
    return isRealDay(y, m, d) ? `${d}/${m}/${y}` : null;
  }
  const instant = INSTANT_RE.exec(value);
  if (!instant) return null;
  const [, y, m, d, hh, mi, ss] = instant;
  if (!isRealDay(y, m, d) || Number(hh) > 23 || Number(mi) > 59 || (ss !== undefined && Number(ss) > 59)) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const parts = formatToPartsMap(date);
  const dateStr = `${parts.day}/${parts.month}/${parts.year}`;
  if (parts.hour === "00" && parts.minute === "00") return dateStr;
  return `${dateStr} ${parts.hour}:${parts.minute}`;
}

function isCnpjDigits(v: string): boolean {
  return /^\d{14}$/.test(v);
}


/** why: chamado recursivamente por formatPairs para qualquer valor aninhado (array de objetos dentro de objeto,
 * objeto dentro de array etc.); nunca usa String() direto num array ou objeto, que é a origem do "[object Object]",
 * nem em booleano, que é a origem do "true"/"false" cru na tela. */
export function formatValue(v: unknown, key?: string, opts: IEvidenceFormatOptions = {}): string {
  if (v === null || v === undefined) return "vazio";
  if (typeof v === "boolean") return v ? "sim" : "não";
  if (typeof v === "string") {
    if (v === "") return "(em branco)";
    if (key && WORKSPACE_ID_KEYS.has(key)) return workspaceLabelOf(v, opts.workspaceLabels ?? {});
    if (key && CNPJ_KEYS.has(key) && isCnpjDigits(v)) return formatCnpj(v);
    const date = formatEvidenceDate(v);
    if (date !== null) return date;
    // hazard: EVIDENCE_ENUM_LABEL[key] sem ownValue acha Object.prototype para key="constructor"
    // (o resultado seria o construtor Object, cujo .name devolveria a string "Object" no lugar do
    // texto cru); ownValue duas vezes garante que só uma chave própria do dicionário conta.
    const enumLabel = key ? ownValue(ownValue(EVIDENCE_ENUM_LABEL, key), v) : undefined;
    if (enumLabel !== undefined) return enumLabel;
    return v;
  }
  if (typeof v === "number") return String(v);
  if (Array.isArray(v)) {
    if (v.length === 0) return "vazio";
    return v.map((item) => formatValue(item, key, opts)).join(", ");
  }
  if (isPlainObject(v)) {
    if (Object.keys(v).length === 0) return "vazio";
    return formatPairs(v, "; ", " → ", opts, key);
  }
  return String(v);
}

/** why: "chave arrow valor" repetido é o formato de um registro; serve tanto para um objeto plano quanto para
 * cada item de uma lista de objetos. parentKey decide o rótulo da chave filha: rótulo do contrato sob
 * WORKSPACE_MAP_KEYS (ex.: porWorkspace), a própria chave sem dicionário sob DATA_KEYED_OBJECTS (chave é dado,
 * não campo: um cargo chamado "total" continua "total"), senão evidenceKeyLabel. */
export function formatPairs(
  obj: Record<string, unknown>,
  separator: string,
  arrow: string,
  opts: IEvidenceFormatOptions = {},
  parentKey?: string,
): string {
  const isWorkspaceMap = !!parentKey && WORKSPACE_MAP_KEYS.has(parentKey);
  const isDataKeyed = !!parentKey && DATA_KEYED_OBJECTS.has(parentKey);
  return Object.entries(obj)
    .map(([k, v]) => {
      const label = isWorkspaceMap ? workspaceLabelOf(k, opts.workspaceLabels ?? {}) : isDataKeyed ? k : evidenceKeyLabel(k);
      const valueKey = isDataKeyed ? undefined : k;
      return `${label}${arrow}${formatValue(v, valueKey, opts)}`;
    })
    .join(separator);
}

/** why: igual ao evidenceText de antes; ignora chaves vazias e emite uma linha por item quando o valor é uma
 * lista de objetos planos (senão o total ficaria escondido dentro de uma linha só de JSON). Chave desconhecida
 * aparece como está; nada é escondido. */
export function evidenceLines(evidence: Record<string, unknown>, opts: IEvidenceFormatOptions = {}): string[] {
  const lines: string[] = [];
  Object.entries(evidence)
    .filter(([, v]) => v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0))
    .forEach(([k, v]) => {
      if (Array.isArray(v) && v.every(isPlainObject)) {
        (v as Record<string, unknown>[]).forEach((item) =>
          lines.push(`${evidenceKeyLabel(k)}: ${formatPairs(item, ", ", ": ", opts, k)}`),
        );
      } else {
        lines.push(`${evidenceKeyLabel(k)}: ${formatValue(v, k, opts)}`);
      }
    });
  return lines;
}

export function evidenceChangeLine(
  change: { path: string; before: unknown; after: unknown },
  opts: IEvidenceFormatOptions = {},
): string {
  const segments = change.path.split(".");
  const label = segments.map((s) => evidenceKeyLabel(s)).join(" / ");
  const lastKey = segments[segments.length - 1];
  return `${label}: de ${formatValue(change.before, lastKey, opts)} para ${formatValue(change.after, lastKey, opts)}`;
}
